import Image from 'next/future/image';
import { useState } from 'react';
import { isUndefined, omitBy } from 'lodash-es';
import cn from 'classnames';
import useRouterTransition from '@/lib/use-router-transition';
import infoIconBlack from '@/public/icons/product/infoIconBlack.svg';
import { ConditionPopup } from '@/components/products/conditionPopup/ConditionPopup';
import { isNaStore } from '@/lib/collections/catalogue/helpers';
import { FilterGroupProps } from './types';

export default function FilterGroup(props: FilterGroupProps) {
  const {
    name,
    values,
    handleFilterChange,
    variantFilters,
    fixedVariantFilters,
    allVisibleFiltersSelected,
    lastAllFiltersUnselected,
    categoryPk,
  } = props;

  const isTransitioning = useRouterTransition();
  const [isConditionOpen, setIsConditionOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(
    [
      ...Object.keys(omitBy(variantFilters, isUndefined)),
      'categories',
    ].includes(props.name) ||
      (lastAllFiltersUnselected?.[props.name] ?? false),
  );

  if (values == null || values.length === 0) return null;

  return (
    <div className="bg-white px-4 pt-4">
      <div className={cn('mb-3 flex items-center justify-between')}>
        <h3
          className="flex w-full cursor-pointer items-center gap-2 text-sm font-bold uppercase text-gray-700"
          onClick={() => setIsOpen(!isOpen)}
        >
          {/* Collapse - Expand Icon */}
          <div>
            <div className="h-[2px] w-[10px] bg-gray-700" />
            <div
              className={cn(
                'mx-auto -mt-[6px] h-[10px] w-[2px] bg-gray-700 transition-all duration-150 ease-in-out',
                {
                  'rotate-90 ': isOpen,
                },
              )}
            />
          </div>
          {name.replace(/([A-Z])/g, ' $1')}
        </h3>
        {/* Learn More */}
        {name === 'condition' && (
          <p
            className="inline-flex shrink-0 cursor-pointer items-center text-[10px] leading-5 text-gray-700 underline"
            onClick={() => setIsConditionOpen(true)}
          >
            <span className="hidden xs:inline">Learn More</span>
            <span className="relative top-[-1px] ml-1 inline-flex cursor-pointer items-center">
              <Image
                src={infoIconBlack}
                alt={infoIconBlack}
                width={13}
                height={13}
                className="object-contain"
              />
            </span>
          </p>
        )}
      </div>
      <div
        className={cn(
          ' overflow-auto transition-all duration-300 ease-in-out',
          {
            'max-h-56': isOpen,
            'max-h-0': !isOpen,
          },
        )}
      >
        {values?.map(({ key, label }) => (
          <label
            key={key}
            className="mb-2 block cursor-pointer text-sm text-gray-700"
          >
            <input
              type="checkbox"
              disabled={isTransitioning}
              className="peer relative top-[2px] mr-2 h-[15px] w-[15px] accent-gray-700"
              checked={
                (fixedVariantFilters[name]?.length ?? 0) > 0 &&
                (variantFilters[name]?.length ?? 0) > 0 &&
                (variantFilters[name]?.length ?? 0) ===
                  (fixedVariantFilters[name]?.length ?? 0)
                  ? allVisibleFiltersSelected?.[name] ?? false
                  : !!variantFilters[name]?.includes(key)
              }
              onChange={(e) => {
                handleFilterChange(
                  name,
                  key,
                  e.target.checked,
                  (fixedVariantFilters[name]?.length ?? 0) > 0,
                );
              }}
            />
            <span className="peer-disabled:opacity-70">
              {label || key.replace(/Oppo/g, 'OPPO')}
            </span>
          </label>
        ))}
      </div>
      <ConditionPopup
        activeCondition={isNaStore ? 'Premium' : 'Brand New'}
        isOpen={isConditionOpen}
        onClose={() => setIsConditionOpen(false)}
        categoryPk={categoryPk}
      />
    </div>
  );
}
